import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/10">
      <div className="container mx-auto px-4 py-8">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <article className="max-w-4xl mx-auto bg-card rounded-lg shadow-lg p-8 mb-8">
          <header className="mb-8 border-b pb-6">
            <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
            <p className="text-muted-foreground">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </header>

          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                Welcome to Her Season. By accessing or using our Service, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                These Terms constitute a legally binding agreement between you and Her Season ("Company," "we," "our," or "us"). We reserve the right to modify these Terms at any time, and your continued use of the Service constitutes acceptance of such modifications.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground leading-relaxed">
                Her Season is a digital wellness platform designed to support women experiencing perimenopause and menopause. Our Service provides:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
                <li>Comprehensive symptom tracking (hot flashes, sleep, mood, triggers, bladder health)</li>
                <li>AI-powered daily reflections and personalized insights based on your tracking data</li>
                <li>Educational resources including micro-learning modules and myth-busting content</li>
                <li>Community forums with peer support and moderated discussions</li>
                <li>SOS toolkit featuring breathing exercises, guided meditations, and immediate relief techniques</li>
                <li>Personalized wellness plans tailored to your journey stage and goals</li>
                <li>E-commerce platform for curated wellness products and merchandise</li>
                <li>Progress tracking and data visualization tools</li>
                <li>Free trial period with optional premium features</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Medical Disclaimer</h2>
              <div className="bg-destructive/10 border-l-4 border-destructive p-4 my-4">
                <p className="text-muted-foreground leading-relaxed font-semibold">
                  IMPORTANT: Her Season is NOT a medical service and does NOT provide medical advice, diagnosis, or treatment.
                </p>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                The information provided through our Service, including AI-generated insights and personalized reflections, is for educational and informational purposes only. It is not intended to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
                <li>Replace professional medical advice, diagnosis, or treatment</li>
                <li>Serve as a substitute for consultation with qualified healthcare providers</li>
                <li>Constitute an endorsement of any particular treatment or therapy</li>
                <li>Provide medical interpretation of your symptoms or health data</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Our symptom tracking and AI-powered insights are tools for self-awareness and discussion with your healthcare provider. Always seek the advice of your physician or other qualified health provider with any questions regarding a medical condition. Never disregard professional medical advice or delay seeking it because of information you receive from our Service.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                If you are experiencing a medical emergency, call your local emergency services immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Eligibility</h2>
              <p className="text-muted-foreground leading-relaxed">
                You must be at least 18 years old to use our Service. By using our Service, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. User Accounts</h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">5.1 Account Registration</h3>
              <p className="text-muted-foreground leading-relaxed">
                To access certain features of our Service, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your password and account</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Not share your account credentials with others</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.2 Free Trial Period</h3>
              <p className="text-muted-foreground leading-relaxed">
                New users receive a 14-day free trial period upon account creation. During this trial:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
                <li>You have access to premium features at no cost</li>
                <li>Your trial period begins when you complete profile setup</li>
                <li>You may cancel at any time during the trial without charge</li>
                <li>After the trial ends, certain features may require a subscription</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.3 Account Termination</h3>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to suspend or terminate your account at any time, with or without notice, for any reason, including violation of these Terms. You may delete your account at any time through your account settings. Upon deletion, your personal data will be removed in accordance with our Privacy Policy, though some anonymized data may be retained for analytics purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. User Conduct and Prohibited Activities</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You agree not to engage in any of the following prohibited activities:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Violating any applicable laws or regulations</li>
                <li>Infringing on intellectual property rights</li>
                <li>Harassing, threatening, or abusing other users</li>
                <li>Posting false, misleading, or malicious content</li>
                <li>Distributing spam, viruses, or malware</li>
                <li>Attempting to gain unauthorized access to our systems</li>
                <li>Scraping or harvesting data from our Service</li>
                <li>Impersonating others or providing false information</li>
                <li>Engaging in commercial activities without authorization</li>
                <li>Interfering with the proper functioning of the Service</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                For detailed community standards, please review our <Link to="/community-guidelines" className="text-primary hover:underline">Community Guidelines</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. User Content</h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">7.1 Your Content</h3>
              <p className="text-muted-foreground leading-relaxed">
                You retain ownership of content you post to our Service ("User Content"), including community posts, comments, daily notes, and symptom tracking data. By posting User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, and display such content for the purpose of operating and improving our Service.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">7.2 Anonymous Posting</h3>
              <p className="text-muted-foreground leading-relaxed">
                Our community features allow you to post anonymously. While your display name may be hidden from other users, we retain the ability to identify you internally for moderation and safety purposes.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">7.3 Content Responsibility</h3>
              <p className="text-muted-foreground leading-relaxed">
                You are solely responsible for your User Content. We do not endorse or guarantee the accuracy of any User Content. We reserve the right to remove any content that violates these Terms or is otherwise objectionable.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">7.4 Content Moderation</h3>
              <p className="text-muted-foreground leading-relaxed">
                We monitor, review, and moderate User Content to maintain a safe and supportive community. We reserve the right to remove content that violates our <Link to="/community-guidelines" className="text-primary hover:underline">Community Guidelines</Link> without notice. Repeated violations may result in account suspension or termination.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. AI-Generated Content and Insights</h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">8.1 AI Features</h3>
              <p className="text-muted-foreground leading-relaxed">
                Our Service uses artificial intelligence to provide personalized daily reflections and insights based on your tracking data. This AI-generated content is:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
                <li>Generated automatically based on patterns in your symptom tracking</li>
                <li>Intended for motivational and educational purposes only</li>
                <li>Not a substitute for professional medical advice</li>
                <li>Subject to limitations and potential inaccuracies inherent in AI systems</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">8.2 Data Usage for AI</h3>
              <p className="text-muted-foreground leading-relaxed">
                By using our Service, you consent to your tracking data being processed by AI systems to generate personalized insights. This data is processed in accordance with our Privacy Policy and is not shared with third parties for purposes other than providing the Service.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">8.3 AI Limitations</h3>
              <p className="text-muted-foreground leading-relaxed">
                You acknowledge that AI-generated content may contain errors, omissions, or inappropriate suggestions. We do not guarantee the accuracy, completeness, or appropriateness of AI-generated insights. Always use your own judgment and consult healthcare professionals for medical decisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                The Service and its original content (excluding User Content), features, and functionality are owned by Her Season and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Our trademarks, logos, and trade dress may not be used without our prior written consent. You may not copy, modify, distribute, sell, or lease any part of our Service, including educational content, SOS toolkit materials, or proprietary wellness tools, without explicit permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Purchases and Payments</h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">10.1 Product Purchases</h3>
              <p className="text-muted-foreground leading-relaxed">
                If you purchase products through our e-commerce platform, you agree to provide accurate payment information and authorize us to charge your payment method for all purchases, including applicable taxes and shipping fees.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">10.2 Subscription Services</h3>
              <p className="text-muted-foreground leading-relaxed">
                Premium features may require a paid subscription. Subscription fees are billed on a recurring basis. You may cancel your subscription at any time, but refunds are not provided for partial subscription periods.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">10.3 Pricing and Availability</h3>
              <p className="text-muted-foreground leading-relaxed">
                All prices are displayed in your local currency and are subject to change without notice. We reserve the right to refuse or cancel orders at our discretion. Product availability is not guaranteed.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">10.4 Refunds and Returns</h3>
              <p className="text-muted-foreground leading-relaxed">
                Physical products: Returns accepted within 30 days of delivery for unused items in original packaging. Shipping costs for returns are the responsibility of the customer unless the item is defective.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-2">
                Digital services and subscriptions: Refunds are generally not provided once services have been rendered or after the trial period ends.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Your privacy is important to us. Please review our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link> to understand how we collect, use, and protect your personal information, including your symptom tracking data and health-related information.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We take data security seriously and implement industry-standard measures to protect your sensitive health information. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Third-Party Links and Services</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our Service may contain links to third-party websites or services. We are not responsible for the content, privacy policies, or practices of third-party sites. You acknowledge and agree that we shall not be liable for any damage or loss caused by your use of third-party services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Disclaimers and Limitations of Liability</h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">12.1 Service "As Is"</h3>
              <p className="text-muted-foreground leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">12.2 Limitation of Liability</h3>
              <p className="text-muted-foreground leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, HER SEASON SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">13. Indemnification</h2>
              <p className="text-muted-foreground leading-relaxed">
                You agree to indemnify, defend, and hold harmless Her Season and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising out of your use of the Service or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">14. Governing Law and Dispute Resolution</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction - To Be Determined], without regard to its conflict of law provisions. 
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Any disputes arising from these Terms or your use of the Service shall first be resolved through good faith negotiations. If negotiations fail, disputes shall be resolved through binding arbitration or in the courts of [Your Jurisdiction - To Be Determined], depending on applicable law and the nature of the dispute.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4 text-sm italic">
                Note: Governing law and jurisdiction will be specified once company registration and legal structure are finalized.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">15. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify or replace these Terms at any time. We will provide notice of material changes by posting the updated Terms on our Service and updating the "Last Updated" date. Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">16. Severability</h2>
              <p className="text-muted-foreground leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">17. Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-secondary/30 rounded-lg">
                <p className="text-muted-foreground">
                  <strong>Her Season</strong><br />
                  Email: support@auralife.com<br />
                  Legal inquiries: legal@auralife.com
                </p>
              </div>
              <p className="text-muted-foreground leading-relaxed mt-4 text-sm">
                Note: Contact information will be updated once company registration is complete.
              </p>
            </section>
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfService;
